// Shared organization definitions used across the application
// This ensures consistency between customer creation, template editing, and other features

export const ORGANIZATION_OPTIONS = [
  { id: 4, name: 'Keystone Glazing' },
  { id: 8, name: 'Keystone South UK' }
] as const;

export type OrganizationOption = typeof ORGANIZATION_OPTIONS[number];

// Helper function to get organization name by ID
export function getOrganizationNameById(id: number): string | undefined {
  return ORGANIZATION_OPTIONS.find(org => org.id === id)?.name;
}

// Helper function to get organization ID by name
export function getOrganizationIdByName(name: string): number | undefined {
  return ORGANIZATION_OPTIONS.find(org => org.name === name)?.id;
}

// Get all organization names as an array
export function getOrganizationNames(): string[] {
  return ORGANIZATION_OPTIONS.map(org => org.name);
}

// Event types that are available for each organization
// If an organization is not listed, all event types are available (backward compatibility)
export const ORGANIZATION_EVENT_TYPES: Partial<Record<string, string[]>> = {
  'Keystone Glazing': [
    'enduranceDoors',
    'roofReplacement',
    'keystoneIntroduction',
    'firstSurveyAppointmentRequest',
    'firstSurveyAppointmentConfirmation',
    'finalSurveyAppointmentRequest',
    'finalSurveyAppointmentConfirmation',
    'installationDateConfirmation',
    'deliveryDateConfirmation',
    'depositRequest',
    'outstandingPayment',
    'otherKeystoneServices',
    'completionEmail',
    'requestReview',
    'newsletter'
  ],
  'Keystone Property': [
    'enduranceDoors',
    'roofReplacement',
    'keystoneIntroduction',
    'firstSurveyAppointmentRequest',
    'firstSurveyAppointmentConfirmation',
    'finalSurveyAppointmentRequest',
    'finalSurveyAppointmentConfirmation',
    'otherKeystoneServices',
    'completionEmail',
    'requestReview',
    'newsletter'
  ]
};

// Helper function to get allowed event types for an organization
export function getAllowedEventTypes(organizationName: string | null | undefined): string[] | null {
  if (!organizationName) return null; // No organization = all event types available
  return ORGANIZATION_EVENT_TYPES[organizationName] || null; // null = all event types available
}

// Helper function to check if an event type is allowed for an organization
export function isEventTypeAllowed(eventType: string, organizationName: string | null | undefined): boolean {
  const allowedTypes = getAllowedEventTypes(organizationName);
  if (allowedTypes === null) return true; // No restriction = all allowed
  return allowedTypes.includes(eventType);
}

// Email addresses for each organization
export const ORGANIZATION_EMAILS: Partial<Record<string, { address: string; name: string }>> = {
  'Keystone Glazing': {
    address: 'info@keystoneglazing.co.uk',
    name: 'Keystone Glazing'
  },
  // Keystone Property has been folded into Keystone South UK
  'Keystone Property': {
    address: 'admin@keystonesouthuk.co.uk',
    name: 'Keystone South UK'
  },
  'Keystone South UK': {
    address: 'admin@keystonesouthuk.co.uk',
    name: 'Keystone South UK'
  }
};

// Sending mailboxes from Admin → Organisations (loaded by organisationSettings). They take
// precedence over ORGANIZATION_EMAILS, which stays as the fallback.
let organizationEmailOverrides: Record<string, { address: string; name: string }> = {};

export function setOrganizationEmailOverrides(next: Record<string, { address: string; name: string }>): void {
  organizationEmailOverrides = { ...next };
}

// Helper function to get email address and name for an organization
export function getOrganizationEmail(organizationName: string | null | undefined): { address: string; name: string } {
  if (organizationName && organizationEmailOverrides[organizationName]) {
    return organizationEmailOverrides[organizationName];
  }
  if (!organizationName) {
    // Default fallback
    return {
      address: 'info@keystoneglazing.co.uk',
      name: 'Keystone Glazing'
    };
  }
  
  return ORGANIZATION_EMAILS[organizationName] || {
    address: 'info@keystoneglazing.co.uk',
    name: organizationName
  };
}

